/**
 * Moveable Divider Interface Component
 * 
 * Provides a drag-and-drop interface for setting the major/minor prize boundary.
 * Replaces individual radio buttons with a single draggable divider that separates
 * major prizes (above) from minor prizes (below).
 * 
 * @module TabPicker.MoveableDividerInterface
 */

import React, { useState, useMemo, useRef } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, closestCenter } from '@dnd-kit/core';
import DragDivider from './DragDivider';
import { cn } from '../../utils/cn';

interface PayoutRow {
  id: string;
  amount: number;
  count: number;
  isMajor: boolean;
}

interface MoveableDividerInterfaceProps {
  payouts: PayoutRow[];
  onUpdatePayouts: (payouts: PayoutRow[]) => void;
  className?: string;
}

export const MoveableDividerInterface: React.FC<MoveableDividerInterfaceProps> = ({
  payouts,
  onUpdatePayouts,
  className
}) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sort payouts by amount (descending) for consistent display
  const sortedPayouts = useMemo(() => {
    return [...payouts].sort((a, b) => b.amount - a.amount);
  }, [payouts]);

  // Calculate divider position based on current isMajor flags
  const dividerPosition = useMemo(() => {
    const majorCount = sortedPayouts.filter(p => p.isMajor).length;
    return majorCount;
  }, [sortedPayouts]);

  // Handle divider position change
  const handlePositionChange = (newPosition: number) => {
    const updatedPayouts = sortedPayouts.map((payout, index) => ({
      ...payout,
      isMajor: index < newPosition
    }));

    // Restore original order for the parent component
    const reorderedPayouts = payouts.map(originalPayout => {
      const updatedPayout = updatedPayouts.find(p => p.id === originalPayout.id);
      return updatedPayout || originalPayout;
    });

    onUpdatePayouts(reorderedPayouts);
  };

  // Handle count change for a specific payout
  const handleCountChange = (payoutId: string, newCount: number) => {
    const updatedPayouts = payouts.map(payout =>
      payout.id === payoutId
        ? { ...payout, count: Math.max(0, newCount) }
        : payout
    );

    onUpdatePayouts(updatedPayouts);
  };

  // Handle amount change for a specific payout
  const handleAmountChange = (payoutId: string, newAmount: number) => {
    const updatedPayouts = payouts.map(payout =>
      payout.id === payoutId
        ? { ...payout, amount: Math.max(0, newAmount) }
        : payout
    );

    onUpdatePayouts(updatedPayouts);
  };

  // Handle drag events with snapping logic
  const handleDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedId(null);
    
    if (!event.delta || !containerRef.current) return;
    
    // Get all prize row elements to calculate actual heights
    const prizeRows = containerRef.current.querySelectorAll('[data-prize-row]');
    if (prizeRows.length === 0) return;
    
    // Calculate average row height from actual elements
    let totalHeight = 0;
    prizeRows.forEach(row => {
      totalHeight += row.getBoundingClientRect().height;
    });
    const avgRowHeight = totalHeight / prizeRows.length;
    
    const dragDistance = event.delta.y;
    const rowsMovedFloat = dragDistance / avgRowHeight;
    
    // Calculate new position with snapping
    // In our list: position 0 = all minor, position N = all major
    // Dragging UP (negative Y) should DECREASE major prizes (decrease position)
    // Dragging DOWN (positive Y) should INCREASE major prizes (increase position)
    let newPosition = dividerPosition + Math.round(rowsMovedFloat);
    
    // Constrain to valid bounds (0 to totalTiers)
    newPosition = Math.max(0, Math.min(sortedPayouts.length, newPosition));
    
    // Only update if position actually changed
    if (newPosition !== dividerPosition) {
      handlePositionChange(newPosition);
    }
  };

  // Don't render if no payouts or only one payout
  if (sortedPayouts.length <= 1) {
    return null;
  }

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={cn("space-y-1", className)}>
        
        {/* Prize list with divider */}
        <div ref={containerRef} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Divider at top (position 0 - all minor) */}
          {dividerPosition === 0 && (
            <>
              <DragDivider
                position={dividerPosition}
                totalTiers={sortedPayouts.length}
                onPositionChange={handlePositionChange}
              />
              <div className="bg-gray-50 px-4 py-3 tablet:py-2 border-b border-gray-200">
                <div className="text-base tablet:text-sm font-medium text-gray-700">
                  Minor Prizes (typically not marked off)
                </div>
              </div>
            </>
          )}
          
          {sortedPayouts.map((payout, index) => (
            <React.Fragment key={payout.id}>
              {/* Major prizes section header */}
              {index === 0 && dividerPosition > 0 && (
                <div className="bg-blue-50 px-4 py-3 tablet:py-2 border-b border-blue-200">
                  <div className="text-base tablet:text-sm font-medium text-blue-800">
                    Major Prizes (may be marked off on flare poster)
                  </div>
                </div>
              )}
              
              {/* Prize row */}
              <div
                data-prize-row
                className={cn(
                  "px-4 py-4 tablet:py-3 border-b border-gray-100 last:border-b-0",
                  payout.isMajor ? "bg-blue-50" : "bg-gray-50"
                )}
              >
                <div className="flex flex-wrap items-center gap-3">
                  {/* Major/Minor badge */}
                  <span className={cn(
                    "inline-block px-2 py-1 rounded text-xs font-medium",
                    payout.isMajor
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  )}>
                    {payout.isMajor ? 'Major' : 'Minor'}
                  </span>

                  {/* Amount input section */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      Prize Amount
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={payout.amount}
                        onChange={(e) => handleAmountChange(payout.id, Math.max(0, parseFloat(e.target.value) || 0))}
                        placeholder="0.00"
                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Count input section */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      # of Prizes
                    </label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleCountChange(payout.id, Math.max(0, payout.count - 1))}
                        className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                        disabled={payout.count <= 0}
                      >
                        −
                      </button>

                      <input
                        type="number"
                        min="0"
                        value={payout.count}
                        onChange={(e) => handleCountChange(payout.id, Math.max(0, parseInt(e.target.value) || 0))}
                        placeholder="0"
                        className="w-14 text-center border border-gray-300 rounded px-1 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />

                      <button
                        onClick={() => handleCountChange(payout.id, payout.count + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Draggable divider (between rows) */}
              {index === dividerPosition - 1 && dividerPosition > 0 && dividerPosition < sortedPayouts.length && (
                <>
                  <DragDivider
                    position={dividerPosition}
                    totalTiers={sortedPayouts.length}
                    onPositionChange={handlePositionChange}
                  />
                  <div className="bg-gray-50 px-4 py-3 tablet:py-2 border-b border-gray-200">
                    <div className="text-base tablet:text-sm font-medium text-gray-700">
                      Minor Prizes (typically not marked off)
                    </div>
                  </div>
                </>
              )}
            </React.Fragment>
          ))}
          
          {/* Divider at bottom (all major) */}
          {dividerPosition === sortedPayouts.length && (
            <DragDivider
              position={dividerPosition}
              totalTiers={sortedPayouts.length}
              onPositionChange={handlePositionChange}
            />
          )}
        </div>
        
        {/* Summary */}
        <div className="text-sm tablet:text-xs text-gray-600 mt-3 tablet:mt-2">
          {dividerPosition === 0 && "All prizes are minor"}
          {dividerPosition === sortedPayouts.length && "All prizes are major"}
          {dividerPosition > 0 && dividerPosition < sortedPayouts.length && 
            `${dividerPosition} major, ${sortedPayouts.length - dividerPosition} minor`
          }
        </div>
      </div>

      {/* Drag overlay for visual feedback */}
      <DragOverlay>
        {draggedId ? <div className="opacity-50">Dragging...</div> : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MoveableDividerInterface;